import { useEffect, useMemo, useState } from "react";
import { getSessionToken } from "../hooks/useSession.js";
import { Button } from "./ui/Button.js";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/Card.js";
import { StatusBadge } from "./ui/StatusBadge.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface SyncRunSummary {
  id: string;
  source: "Pinnacle" | "Reagan";
  status: string;
  triggerType: string;
  startedAt: string;
  finishedAt: string | null;
  fetchedCount: number;
  upsertedCount: number;
  fetchedContractCount: number;
  upsertedContractCount: number;
  errorMessage: string | null;
}

function runStatusBadge(status: string) {
  const normalized = status.toLowerCase();
  if (normalized === "done" || normalized === "completed" || normalized === "success") return "ACTIVE";
  if (normalized === "failed" || normalized === "error") return "REJECTED";
  if (normalized === "running" || normalized === "processing") return "PENDING";
  return "PENDING";
}

function formatWhen(value: string | null) {
  if (!value) return "In progress";
  return new Date(value).toLocaleString();
}

export function SyncRunsOverview() {
  const token = useMemo(() => getSessionToken(), []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<SyncRunSummary[]>([]);
  const [running, setRunning] = useState<string | null>(null);

  async function loadRuns() {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const headers = { Authorization: `Bearer ${token}` };
      const [pinnacleRes, reaganRes] = await Promise.all([
        fetch(`${API_BASE}/api/pinnacle/sync/runs?limit=5`, { headers }),
        fetch(`${API_BASE}/api/reagan/sync/runs?limit=5`, { headers }),
      ]);
      if (!pinnacleRes.ok || !reaganRes.ok) throw new Error("Failed to load sync runs");

      const [pinnacleJson, reaganJson] = await Promise.all([pinnacleRes.json(), reaganRes.json()]);
      const pinnacleRuns = ((pinnacleJson.data ?? []) as Omit<SyncRunSummary, "source">[]).map((run) => ({
        ...run,
        source: "Pinnacle" as const,
      }));
      const reaganRuns = ((reaganJson.data ?? []) as Omit<SyncRunSummary, "source">[]).map((run) => ({
        ...run,
        source: "Reagan" as const,
      }));
      setRuns(
        [...pinnacleRuns, ...reaganRuns].sort(
          (left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime()
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load sync runs");
    } finally {
      setLoading(false);
    }
  }

  async function triggerSync(source: "Pinnacle" | "Reagan") {
    if (!token) return;
    setRunning(source);
    try {
      const path = source === "Pinnacle" ? "/api/pinnacle/sync/run" : "/api/reagan/sync/run";
      const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || `Failed to start ${source} sync`);
      }
      await loadRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to start sync");
    } finally {
      setRunning(null);
    }
  }

  useEffect(() => {
    loadRuns();
    const id = setInterval(loadRuns, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const latestBySource = useMemo(() => {
    const pinnacle = runs.find((run) => run.source === "Pinnacle");
    const reagan = runs.find((run) => run.source === "Reagan");
    return { pinnacle, reagan };
  }, [runs]);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-content-muted">Recent runs</h2>
          <p className="text-sm text-content-secondary">Latest sync status across upstream sources.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" disabled={running === "Pinnacle"} onClick={() => triggerSync("Pinnacle")}>
            {running === "Pinnacle" ? "Starting…" : "Run Pinnacle"}
          </Button>
          <Button size="sm" variant="secondary" disabled={running === "Reagan"} onClick={() => triggerSync("Reagan")}>
            {running === "Reagan" ? "Starting…" : "Run Reagan"}
          </Button>
          <Button size="sm" variant="ghost" onClick={loadRuns}>Refresh</Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-status-rejected-border bg-status-rejected-bg p-3 text-sm text-status-rejected-text">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1].map((key) => (
            <Card key={key} className="animate-pulse h-36" padding="md" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {(["Pinnacle", "Reagan"] as const).map((source) => {
            const run = source === "Pinnacle" ? latestBySource.pinnacle : latestBySource.reagan;
            return (
              <Card key={source}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle>{source}</CardTitle>
                    {run ? <StatusBadge status={runStatusBadge(run.status)} size="sm" /> : null}
                  </div>
                  <CardDescription>
                    {run ? `Last run ${formatWhen(run.finishedAt ?? run.startedAt)}` : "No runs recorded yet"}
                  </CardDescription>
                </CardHeader>
                {run ? (
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-content-muted text-xs uppercase tracking-wider">Agents</dt>
                      <dd className="text-content-primary">{run.upsertedCount} / {run.fetchedCount}</dd>
                    </div>
                    <div>
                      <dt className="text-content-muted text-xs uppercase tracking-wider">Contracts</dt>
                      <dd className="text-content-primary">{run.upsertedContractCount} / {run.fetchedContractCount}</dd>
                    </div>
                    <div>
                      <dt className="text-content-muted text-xs uppercase tracking-wider">Trigger</dt>
                      <dd className="text-content-primary capitalize">{run.triggerType}</dd>
                    </div>
                    <div>
                      <dt className="text-content-muted text-xs uppercase tracking-wider">Started</dt>
                      <dd className="text-content-primary">{formatWhen(run.startedAt)}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-sm text-content-muted">Trigger a sync to populate cache tables.</p>
                )}
                {run?.errorMessage && (
                  <p className="mt-3 text-xs text-status-rejected-text">{run.errorMessage}</p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
